import { reportTokenRequestDiagnostic } from '@/adapters/errorReporterAdapter';
import { getMonotonicElapsedMs } from '../tokenResponseDiagnostics';
import type { TokenRecoveryDiagnostic, TokenRecoveryReason } from '../tokenRecoveryDiagnostics';
import type { TokenRefreshAttemptResult } from './types';

const MAX_REPORTED_RECOVERY_FAILURES = 32;

export const createCoordinationFailure = (
  result: TokenRefreshAttemptResult,
  recovery: TokenRecoveryDiagnostic | undefined,
  reason: TokenRecoveryReason
): TokenRefreshAttemptResult => {
  if (!recovery) {
    return result;
  }

  return {
    ...result,
    diagnosticSummary: {
      operation: 'refresh',
      responseReceived: false,
      failureAt: 'coordination',
      recovery: { ...recovery, reason }
    }
  };
};

export class CoordinationRecoveryReporter {
  private readonly tabId: string;

  private readonly reportedRecoveryFailures = new Set<string>();

  constructor(tabId: string) {
    this.tabId = tabId;
  }

  public reportFailure(
    result: TokenRefreshAttemptResult,
    recovery: TokenRecoveryDiagnostic,
    startedAt: number,
    tokenUsable: boolean | undefined
  ): void {
    const diagnostic = result.diagnosticSummary;

    if (
      result.terminalFailure ||
      diagnostic?.failureAt !== 'coordination' ||
      tokenUsable !== false
    ) {
      return;
    }

    diagnostic.recovery = {
      ...(diagnostic.recovery || recovery),
      tabId: this.tabId,
      trigger: recovery.trigger,
      role: recovery.role,
      tokenUsable,
      elapsedMs: getMonotonicElapsedMs(startedAt)
    };

    // Several local callers can be waiting for the same owner recovery.
    const reportKey = `${diagnostic.recovery.recoveryId}:${diagnostic.recovery.reason}`;

    if (this.reportedRecoveryFailures.has(reportKey)) {
      return;
    }

    if (this.reportedRecoveryFailures.size >= MAX_REPORTED_RECOVERY_FAILURES) {
      const oldestReportKey = this.reportedRecoveryFailures.values().next().value!;
      this.reportedRecoveryFailures.delete(oldestReportKey);
    }

    this.reportedRecoveryFailures.add(reportKey);
    reportTokenRequestDiagnostic(diagnostic, 'recovery_exhausted');
  }
}
